import React from "react";
import { Box, FormControl, Grid, Link, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link as RouterLink } from "react-router-dom";

import { getAuthenticationRequestSchema, type AuthenticationRequestData } from "../../../schemas";
import { ApplicationWordArt, AuthenticationButton, StyledFormLabel, StyledTextField } from "../../../components";

export interface PasswordResetFormProps {
	readonly onSubmit: (data: AuthenticationRequestData) => void;
	readonly defaultUsername: string;
	readonly isError: boolean;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onSubmit, defaultUsername, isError }) => {
	const intl = useIntl();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<AuthenticationRequestData>({
		resolver: zodResolver(getAuthenticationRequestSchema(intl)),
		defaultValues: {
			username: defaultUsername,
		},
	});

	return (
		<Grid container component="form" onSubmit={handleSubmit(onSubmit)} direction="column" gap={6}>
			<Box sx={{ margin: "auto" }}>
				<ApplicationWordArt width={300} />
			</Box>

			<Grid container direction="column" alignItems="center">
				<Typography variant="h1" color="white">
					<FormattedMessage id="password-reset.title" defaultMessage="Reset password" />
				</Typography>
				<Typography color="white">
					<FormattedMessage
						id="password-reset.instruction"
						defaultMessage="Enter your email below to reset your password"
					/>
				</Typography>
			</Grid>

			<Grid container direction="column" gap={2}>
				<FormControl error={isError || Boolean(errors.username)}>
					<StyledFormLabel color="white">
						<FormattedMessage id="password-reset.email" defaultMessage="Email" />
					</StyledFormLabel>
					<StyledTextField
						placeholder={intl.formatMessage({
							id: "password-reset.email.placeholder",
							defaultMessage: "Enter your email address",
						})}
						fullWidth
						autoFocus
						{...register("username")}
						error={isError || Boolean(errors.username)}
						helperText={errors.username?.message}
						slotProps={{
							formHelperText: {
								"data-testid": "password-reset__text__email-error",
							},
						}}
					/>
				</FormControl>

				{Boolean(isError) && (
					<Typography color="error">
						<FormattedMessage
							id="password-reset.error"
							defaultMessage="Unable to send password reset email. Please try again."
						/>
					</Typography>
				)}

				<Grid container gap={1} direction="column">
					<AuthenticationButton variant="contained" type="submit" fullWidth size="large">
						<FormattedMessage id="password-reset.submit" defaultMessage="Next" />
					</AuthenticationButton>

					<Link
						to={`/login-password?username=${encodeURIComponent(defaultUsername)}`}
						component={RouterLink}
						sx={{ alignSelf: "flex-end", color: "white" }}
					>
						<FormattedMessage id="password-reset.back" defaultMessage="Back to login" />
					</Link>
				</Grid>
			</Grid>
		</Grid>
	);
};
